import { supabase, supabaseAdmin } from '../lib/supabase';

class AlunosService {
  // Buscar todos os alunos com dados relacionados
  async getAll() {
    try {
      const { data, error } = await supabase
        .from('alunos')
        .select(`
          *,
          escolas:escola_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome)
        `)
        .order('nome');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar alunos:', error);
      throw error;
    }
  }

  // Buscar aluno por ID
  async getById(id) {
    try {
      const { data, error } = await supabase
        .from('alunos')
        .select(`
          *,
          escolas:escola_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao buscar aluno:', error);
      throw error;
    }
  }

  // Buscar aluno por email
  async getByEmail(email) {
    try {
      const { data, error } = await supabase
        .from('alunos')
        .select('*')
        .eq('email', email)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    } catch (error) {
      console.error('Erro ao buscar aluno por email:', error);
      throw error;
    }
  }

  // Buscar aluno por CPF
  async getByCPF(cpf) {
    try {
      const { data, error } = await supabase
        .from('alunos')
        .select('*')
        .eq('cpf', cpf)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    } catch (error) {
      console.error('Erro ao buscar aluno por CPF:', error);
      throw error;
    }
  }

  // Criar novo aluno
  async create(alunoData) {
    try {
      // Hash da senha antes de salvar
      const hashedPassword = await this.hashPassword(alunoData.senha);
      
      const { data, error } = await supabase
        .from('alunos')
        .insert([{
          ...alunoData,
          senha: hashedPassword
        }])
        .select(`
          *,
          escolas:escola_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao criar aluno:', error);
      throw error;
    }
  }

  // Atualizar aluno
  async update(id, alunoData) {
    try {
      // Se a senha foi alterada, fazer hash
      if (alunoData.senha) {
        alunoData.senha = await this.hashPassword(alunoData.senha);
      }

      const { data, error } = await supabase
        .from('alunos')
        .update(alunoData)
        .eq('id', id)
        .select(`
          *,
          escolas:escola_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao atualizar aluno:', error);
      throw error;
    }
  }

  // Excluir aluno
  async delete(id) {
    try {
      const { error } = await supabase
        .from('alunos')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erro ao excluir aluno:', error);
      throw error;
    }
  }

  // Ativar/Desativar aluno
  async toggleActive(id, ativo) {
    try {
      const { data, error } = await supabase
        .from('alunos')
        .update({ ativo })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao alterar status do aluno:', error);
      throw error;
    }
  }

  // Buscar escolas para dropdown
  async getEscolas() {
    try {
      const { data, error } = await supabase
        .from('escolas')
        .select('id, nome')
        .order('nome');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar escolas:', error);
      throw error;
    }
  }

  // Buscar anos escolares para dropdown
  async getAnosEscolares() {
    try {
      const { data, error } = await supabase
        .from('anos_escolares')
        .select('id, nome')
        .order('nome');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar anos escolares:', error);
      throw error;
    }
  }

  // Criar usuário de autenticação para o aluno
  async createAuthUser(email, password, userData) {
    try {
      console.log('🔐 Criando usuário de autenticação com Admin API para aluno:', email);
      console.log('📋 Dados do aluno:', userData);

      // Usar Admin API para criar usuário sem fazer login automático
      const { data: authData, error: authError } = await supabaseAdmin.auth.admin.createUser({
        email: email,
        password: password,
        user_metadata: {
          name: userData.nome,
          role: 'student'
        },
        email_confirm: true // Confirmar email automaticamente
      });

      console.log('📊 Resultado do Admin createUser:', { authData, authError });

      if (authError) {
        console.error('❌ Erro no Admin createUser:', authError);
        throw authError;
      }

      console.log('✅ Usuário criado com Admin API:', authData.user?.email);

      // Se o usuário foi criado com sucesso, criar o perfil
      if (authData.user) {
        console.log('📝 Criando perfil para usuário:', authData.user.id);
        await this.createProfile(authData.user.id, userData);
        console.log('✅ Perfil criado com sucesso');
      }

      // Verificar se o admin ainda está logado (deve estar)
      const { data: currentUser } = await supabase.auth.getUser();
      console.log('🔍 Admin ainda logado:', currentUser?.user?.email);

      return authData;
    } catch (error) {
      console.error('❌ Erro ao criar usuário de autenticação:', error);
      console.error('📋 Stack trace:', error.stack);
      throw error;
    }
  }

  // Criar perfil do aluno
  async createProfile(userId, alunoData) {
    try {
      console.log('📝 Iniciando criação de perfil...');
      console.log('🆔 User ID:', userId);
      console.log('📋 Dados do aluno para perfil:', alunoData);

      const profileData = {
        id: userId,
        aluno_id: alunoData.id,
        email: alunoData.email,
        name: alunoData.nome,
        phone: alunoData.telefone || null,
        department: 'Aluno',
        role: 'student',
        is_active: alunoData.ativo || true
      };

      console.log('📊 Dados do perfil a serem inseridos:', profileData);

      const { data, error } = await supabase
        .from('aluno_profiles')
        .insert([profileData])
        .select()
        .single();

      console.log('📊 Resultado da inserção do perfil:', { data, error });

      if (error) {
        console.error('❌ Erro detalhado na criação do perfil:', error);
        throw error;
      }

      console.log('✅ Perfil criado com sucesso:', data);
      return data;
    } catch (error) {
      console.error('❌ Erro ao criar perfil do aluno:', error);
      console.error('📋 Stack trace do perfil:', error.stack);
      throw error;
    }
  }

  // Função auxiliar para hash da senha
  async hashPassword(password) {
    // Em um ambiente real, você usaria bcrypt ou similar
    // Por simplicidade, vamos usar uma implementação básica
    // IMPORTANTE: Em produção, use uma biblioteca adequada para hash de senhas
    return btoa(password); // Base64 encoding - NÃO use em produção!
  }

  // Verificar senha
  async verifyPassword(password, hashedPassword) {
    return btoa(password) === hashedPassword;
  }

  // Login do aluno
  async login(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro no login:', error);
      throw error;
    }
  }

  // Buscar perfil do aluno logado
  async getCurrentProfile() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return null;

      const { data, error } = await supabase
        .from('aluno_profiles')
        .select(`
          *,
          alunos:aluno_id(*)
        `)
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao buscar perfil atual:', error);
      throw error;
    }
  }

  // Buscar perfil do usuário atual (para controle de permissões)
  async getCurrentUserProfile() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return null;

      // Primeiro buscar o perfil
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      // Se tem school_id, buscar a escola separadamente
      if (profile && profile.school_id) {
        const { data: escola, error: escolaError } = await supabase
          .from('escolas')
          .select('id, nome')
          .eq('id', profile.school_id)
          .single();

        if (!escolaError && escola) {
          profile.escolas = escola;
        }
      }

      return profile;
    } catch (error) {
      console.error('Erro ao buscar perfil do usuário atual:', error);
      return null;
    }
  }

  // Filtrar alunos
  async filter(filters = {}) {
    try {
      let query = supabase
        .from('alunos')
        .select(`
          *,
          escolas:escola_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome)
        `);

      // Aplicar filtros
      if (filters.escola_id) {
        query = query.eq('escola_id', filters.escola_id);
      }

      if (filters.ano_escolar_id) {
        query = query.eq('ano_escolar_id', filters.ano_escolar_id);
      }

      if (filters.periodo) {
        query = query.eq('periodo', filters.periodo);
      }

      if (filters.ativo !== undefined) {
        query = query.eq('ativo', filters.ativo);
      }

      if (filters.search) {
        query = query.or(`nome.ilike.%${filters.search}%,email.ilike.%${filters.search}%,cpf.ilike.%${filters.search}%`);
      }

      const { data, error } = await query.order('nome');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao filtrar alunos:', error);
      throw error;
    }
  }
}

export default new AlunosService();
